import { usePageActions, PageContext } from "../../PageContent";
import { BlockButton, Breakpoints, ConfirmationModal, Icon, IconAndText, IconDefinitions, ODataGrid, ResponsiveGridColumn, SpinnerDefault, TitlelessCard } from "../../../components";
import { useCallback, useRef, useState } from "react";
import { DisciplineModel, ODataResponse } from "../../../data/entities";
import { useOutletContext } from "react-router-dom";
import { AddDisciplineModal, ManageDisciplineModal } from "./";
import { useSharedState } from "../../../app/sharedProperty";
import { showSuccessMessage } from "../../../app/tools";
import { MenuItem, MenuSelectEvent } from "@progress/kendo-react-layout";
import { DisciplineService } from "../../../services";
import { GridCellProps } from "@progress/kendo-react-grid";

export function SettingsDisciplinesCard() {

  const context: PageContext = useOutletContext();
  context.setTitle("Settings: Disciplines");
  
  const gridData = useSharedState<Array<DisciplineModel>>([]);
  const gridRef = useRef<ODataGrid>(null);
  const showAddState = useSharedState(false);
  const showEditState = useSharedState(false);
  const showDeleteState = useSharedState(false);
  const editItemState = useSharedState<DisciplineModel>({} as DisciplineModel)
  const deleteItemState = useSharedState<DisciplineModel>({} as DisciplineModel)
  const [showSpinner, setShowSpinner] = useState(false);

  const disciplineService = new DisciplineService();

  usePageActions(
    <BlockButton onClick={() => { showAddState.setter(true); }}>
      <IconAndText iconName={IconDefinitions.add} text="Add New Discipline" />
    </BlockButton>
  );

  const getDisciplines = (oDataQuery: string): Promise<ODataResponse<any>> =>
     disciplineService.getDisciplinesAsync(oDataQuery);

  const onRowMenuSelect = (e: MenuSelectEvent, dataItem: any) => {
    if (dataItem.isDeleted) return;
    switch (e.item.data.action){
      case "edit":
        showEdit(dataItem);
        break;
      case "delete":
        showDelete(dataItem);
    }
  }

  const showEdit = (dataItem: DisciplineModel) => {
    editItemState.setter(dataItem);
    showEditState.setter(true);
  }

  const showDelete = (dataItem: DisciplineModel) => {
    deleteItemState.setter(dataItem);
    showDeleteState.setter(true);
  }

  const addDiscipline = async (discipline: DisciplineModel) => {
    console.log("Add Discipline: ", discipline);
    try {
      showAddState.setter(false);
      setShowSpinner(true);
      await disciplineService.addDisciplineAsync(discipline);
      gridRef.current?.refreshData();
      showSuccessMessage("Discipline added successfully.");
    } catch (err) {
      throw new Error(`Unable to add new discipline, ${err}`);
    } finally {
      setShowSpinner(false);
    }
  }

  const editDiscipline = async (discipline: DisciplineModel) => {
    setShowSpinner(true);
    try{
      showEditState.setter(false);
      await disciplineService.editDisciplineAsync(discipline);
      gridRef.current?.refreshData();
      showSuccessMessage("Discipline updated successfully.");
    } catch (err) {
      throw new Error(`Unable to edit discipline: ${err}`);
    } finally {
      setShowSpinner(false);
    }
  }

  const deleteDiscipline = async (discipline: DisciplineModel) => {
    setShowSpinner(true);
    try {
      console.log("Discipline: ", discipline);
      await disciplineService.deleteDisciplineByIdAsync(discipline.id);
      gridRef.current?.refreshData();
      showSuccessMessage("Discipline deleted successfully.");
    } catch(err) {
      throw new Error(`Unable to delete discipline: ${err}`);
    } finally {
      setShowSpinner(false);
    }
  }

  const commandsCell = useCallback(
    (props: GridCellProps) => (
      <td className="text-center">
        <Icon
          iconName={IconDefinitions.delete}
          className="text-danger"
          onClick={() => showDelete(props.dataItem)}
        />
      </td>
    ),
    [showDelete]
  );

  return (
    <>
      <SpinnerDefault show={showSpinner} fullscreen={true} />
      <TitlelessCard bodyClassName="p-0">
        <ODataGrid
          ref={gridRef}
          fullHeight={true}
          getData={getDisciplines}
          dataState={gridData}
          onRowMenuSelect={onRowMenuSelect}
          onRowClick={(e) => { showEdit(e.dataItem); }}
          sort={[{ field: "name", dir: "desc"}]}
          scrollable="scrollable"
          pageSize={100}
        >
          <ResponsiveGridColumn
            title="Name"
            field="name"
            sortable={true}
          />
          <ResponsiveGridColumn
            title="Code"
            field="code"
            sortable={true}
          />
          <ResponsiveGridColumn
            className="column-url"
            field="url"
            title=" "
            sortable={false}
            filterable={false}
            headerCell={() => null}
            cell={commandsCell}
            width={50}
            breakpoint={Breakpoints.sm}
          />
          <MenuItem
            text="Edit"
            data={{ action: "edit" }}
            icon="hyperlink-open"
          />
          <MenuItem
            text="Delete"
            data={{ action: "delete" }}
            icon={IconDefinitions.delete}
          />
        </ODataGrid>
      </TitlelessCard>
      <AddDisciplineModal
        showState={showAddState}
        onSubmit={addDiscipline}
      />
      <ManageDisciplineModal
        showState={showEditState}
        dataItemState={editItemState}
        onSubmit={editDiscipline}
      />
      <ConfirmationModal
        showState={showDeleteState}
        title={`Delete Discipline ${deleteItemState.value?.name}`}
        message={`You are about to delete this discipline, it cannot be undone. Are you sure you want to delete discipline ${deleteItemState.value.name}?`}
        confirmText="Delete"
        onConfirm={() => deleteDiscipline(deleteItemState.value)}
      />
    </>
  );
}

export default SettingsDisciplinesCard;

